import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  TablePagination,
  Tooltip,
  Checkbox,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import assessmentService from '../services/assessmentService';
import teamService from '../services/teamService';
import { useAuth } from '../contexts/AuthContext';
import { surveyResultService, FilteredSurveyResult } from '../services/surveyResultService';

type SurveyResult = FilteredSurveyResult;

interface FilterCriteria {
  searchTerm: string;
  assessmentId: number | '';
  category: string;
  riskLevel: string;
  teamId: number | '';
  dateFrom: Date | null;
  dateTo: Date | null;
  showOnlyFlagged: boolean;
}

const FilterResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState<SurveyResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedResults, setSelectedResults] = useState<number[]>([]);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [categories] = useState(['Depression', 'Anxiety', 'Mental Health', 'Risk Assessment']);
  
  const [filters, setFilters] = useState<FilterCriteria>({
    searchTerm: '',
    assessmentId: '',
    category: '',
    riskLevel: '',
    teamId: '',
    dateFrom: null,
    dateTo: null,
    showOnlyFlagged: false
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadResults();
  }, [page, rowsPerPage, filters]);

  const loadInitialData = async () => {
    try {
      const [assessmentsRes, teamsRes] = await Promise.all([
        assessmentService.getAssessments(0, 100),
        user?.userType === 'admin' ? teamService.getMyTeams() : Promise.resolve([])
      ]);
      
      setAssessments(assessmentsRes.content);
      setTeams(teamsRes);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadResults = async () => {
    try {
      setLoading(true);
      
      // Build query parameters based on filters
      const params: any = {
        page,
        size: rowsPerPage
      };
      
      // Filters are now handled by the service
      
      // Call the real API
      const response = await surveyResultService.getFilteredResults({
        ...filters,
        page,
        size: rowsPerPage
      });
      
      setResults(response.content);
      setTotalElements(response.totalElements);
      
    } catch (error) {
      console.error('Error loading results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof FilterCriteria, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page on filter change
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedResults(results.map(r => r.id));
    } else {
      setSelectedResults([]);
    }
  };

  const handleSelectResult = (resultId: number) => {
    setSelectedResults(prev => {
      if (prev.includes(resultId)) {
        return prev.filter(id => id !== resultId);
      } else {
        return [...prev, resultId];
      }
    });
  };

  const handleExportSelected = async (format: 'csv' | 'pdf') => {
    if (selectedResults.length === 0) {
      toast.error('Please select results to export');
      return;
    }
    
    try {
      const blob = await surveyResultService.exportResults(selectedResults, format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `survey_results_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedResults.length} results as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleViewResult = (result: SurveyResult) => {
    navigate(`/provider/patients/${result.studentId}/assessments/${result.assessmentId}/results`);
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  const getRiskLevelIcon = (level: string) => {
    switch (level) {
      case 'HIGH': return <ErrorIcon />;
      case 'MEDIUM': return <WarningIcon />;
      case 'LOW': return <CheckIcon />;
      default: return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Filter Survey Results
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
          {selectedResults.length > 0 && (
            <>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleExportSelected('csv')}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={() => handleExportSelected('pdf')}
                color="secondary"
              >
                Export PDF
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Collapse in={showFilters}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Search by student name, email, or ID..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Assessment</InputLabel>
                  <Select
                    value={filters.assessmentId}
                    onChange={(e) => handleFilterChange('assessmentId', e.target.value)}
                    label="Assessment"
                  >
                    <MenuItem value="">All Assessments</MenuItem>
                    {assessments.map((assessment) => (
                      <MenuItem key={assessment.id} value={assessment.id}>
                        {assessment.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Risk Level</InputLabel>
                  <Select
                    value={filters.riskLevel}
                    onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
                    label="Risk Level"
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    <MenuItem value="HIGH">High Risk</MenuItem>
                    <MenuItem value="MEDIUM">Medium Risk</MenuItem>
                    <MenuItem value="LOW">Low Risk</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {user?.userType === 'admin' && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Team</InputLabel>
                    <Select
                      value={filters.teamId}
                      onChange={(e) => handleFilterChange('teamId', e.target.value)}
                      label="Team"
                    >
                      <MenuItem value="">All Teams</MenuItem>
                      {teams.map((team) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="From Date"
                    value={filters.dateFrom}
                    onChange={(newValue) => handleFilterChange('dateFrom', newValue)}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="To Date"
                    value={filters.dateTo}
                    onChange={(newValue) => handleFilterChange('dateTo', newValue)}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.showOnlyFlagged}
                      onChange={(e) => handleFilterChange('showOnlyFlagged', e.target.checked)}
                    />
                  }
                  label="Show only flagged results"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : results.length === 0 ? (
        <Alert severity="info">
          No results found matching your filter criteria.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedResults.length > 0 && selectedResults.length < results.length}
                      checked={results.length > 0 && selectedResults.length === results.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Assessment</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Risk Level</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result) => (
                  <TableRow key={result.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedResults.includes(result.id)}
                        onChange={() => handleSelectResult(result.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{result.studentName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {result.schoolId} • {result.studentEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>{result.assessmentTitle}</TableCell>
                    <TableCell>{result.category}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {result.score} / {result.maxScore}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round((result.score / result.maxScore) * 100)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={getRiskLevelIcon(result.riskLevel) || undefined}
                        label={result.riskLevel}
                        color={getRiskLevelColor(result.riskLevel) as any}
                        size="small"
                      />
                      {result.flags && result.flags.length > 0 && (
                        <Box mt={0.5}>
                          {result.flags.map((flag, index) => (
                            <Chip
                              key={index}
                              label={flag}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(result.completedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{result.teamName || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewResult(result)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
    </Container>
  );
};

export default FilterResultsPage;